'use strict'

const _ = require('lodash')
const groupNames = require('./groupNames.json')
const fileGroups = require('./fileGroups.json')

/**
 * List of objects indexed by extension
 * @prop {string} type MIME type
 * @prop {boolean} preview Is a previewable type
 * @prop {string} group The name of the group this type is included in
 */
let extensionList

/**
 * List of objects indexed by MIME type
 * @prop {string} ext File extension
 * @prop {boolean} preview Is a previewable type
 * @prop {string} group The name of the group this type is included in
 */
let mimetypeList

setupLists()

/**
 * @typedef {object} mimetypeParams
 * @prop {string} type Content MIME type
 * @prop {string} fallbackExt Fallback extension
 * @prop {string} fallbackFileName Fallback file name
 */

module.exports = {
  getGroupNames: getGroupNames,
  getGroupName: getGroupName,
  getGroupLabels: getGroupLabels,
  getGroupLabel: getGroupLabel,
  getExtension: getExtension,
  getTypesForGroup: getTypesForGroup,
  hasPreview: hasPreview,
  typeExists: typeExists,
  getValidTypes: getValidTypes,
}

/**
 * Gets list of names for groups of mimetypes
 *
 * @returns {object}
 */
function getGroupNames() {
  return groupNames
}

/**
 * Gets name of group file belongs to
 *
 * @param {mimetypeParams} mimetypeParams MIME type parameters
 * @returns {string}
 */
function getGroupName(mimetypeParams) {
  return groupNames[getGroupLabel(mimetypeParams)]
}

/**
 * Gets list of labels for groups
 *
 * @returns {array}
 */
function getGroupLabels() {
  return _.keys(fileGroups)
}

/**
 * Gets label for group the file belongs to
 *
 * @param {mimetypeParams} mimetypeParams MIME type parameters
 * @returns {string}
 */
function getGroupLabel(mimetypeParams) {
  return lookupPropertyInLists(mimetypeParams, 'group') || 'attachment'
}

/**
 * Gets extension for a given filename
 *
 * @param {string} fileName
 * @returns {string} Extracted extension
 */
function getExtension(fileName) {
  if (!fileName) {
    return null
  }

  const ext = fileName.match(/\.\w+$/g)
  return ext && ext.length > 0 ? ext[0] : null
}

/**
 * Gets list of mimetypes for a group
 *
 * @param {string} groupLabel
 * @returns {array}
 */
function getTypesForGroup(groupLabel) {
  const types = fileGroups[groupLabel]

  if (!types) {
    return null
  }

  return _.uniq(_.map(types, 'type'))
}

/**
 * Checks if file type is considered previewable
 *
 * @param {mimetypeParams} mimetypeParams MIME type parameters
 * @returns {boolean}
 */
function hasPreview(mimetypeParams) {
  return !!lookupPropertyInLists(mimetypeParams, 'preview')
}

/**
 * Checks if type exists
 *
 * @param {mimetypeParams} mimetypeParams MIME type parameters
 * @returns {boolean}
 */
function typeExists(mimeType) {
  return mimetypeList.hasOwnProperty(mimeType)
}

/**
 * Gets list of types for all groups
 *
 * @returns {array}
 */
function getValidTypes() {
  return _.keys(mimetypeList).sort()
}

/**
 * Prepopulates lists of file type definitions
 * (see list definitions at the top of the file)
 */
function setupLists() {
  extensionList = {}
  mimetypeList = {}

  _.each(fileGroups, function (group, groupName) {
    _.each(group, function (typeDef) {
      const extensionListTypeDef = {
        type: typeDef.type,
        preview: typeDef.preview,
        group: groupName,
      }
      addToList(extensionList, extensionListTypeDef, typeDef.ext)

      const typeListTypeDef = {
        ext: typeDef.ext,
        preview: typeDef.preview,
        group: groupName,
      }
      addToList(mimetypeList, typeListTypeDef, typeDef.type)
    })
  })
}

/**
 * Adds a type definition to a given object indexed by a given property
 */
function addToList(list, typeDef, indexedBy) {
  if (list[indexedBy]) {
    list[indexedBy].push(typeDef)
  } else {
    list[indexedBy] = [typeDef]
  }
}

/**
 * For a given set of type params, looks up a property in both
 * the lists ordered by type or extension
 */
function lookupPropertyInLists(mimetypeParams, property) {
  const type = mimetypeParams.type

  if (type) {
    const types = mimetypeList[type]

    if (types && types.length > 0) {
      return types[0][property]
    }
  }

  const ext = determineExtension(mimetypeParams.fallbackExt, mimetypeParams.fallbackFileName)

  if (ext) {
    const extensions = extensionList[ext]

    if (extensions && extensions.length > 0) {
      return extensions[0][property]
    }
  }
}

function determineExtension(ext, fallbackFileName) {
  if (!ext && fallbackFileName) {
    ext = getExtension(fallbackFileName)
  }

  return ext ? (ext && /^\./.test(ext) ? ext : '.' + ext) : null
}
